import { customElement } from 'lit/decorators.js';
import { FabricationPurchaseOrderState, PurchaseOrderSummaryView } from '../../../api/fabrication-purchase-order-api';
import {
  FabricationPurchaseOrderSummaryTable,
  FabricationPurchaseOrderSummaryTableOptions
} from './fabrication-purchase-order-summary-table';
import { html } from 'lit';
import { LangFabricationPurchaseOrder } from '../../../../localization/scripts/fabrication-purchase-orders/views/fabrication-purchase-order-lang';
import {
  ModalPickerDialog,
  type ModalPickerDialogOptions
} from '../../../../webmodule-common/other/ui/modal-picker-base';
import type { NullPromise } from '../../../../webmodule-common/other/ui/events';
import type {
  WebModuleTable,
  WebModuleTableColumnDef
} from '../../../../webmodule-common/components/src/webmodule-components';

const createNewSentinel = 'create-new' as const;
export type DraftPurchaseOrderPickerResult = PurchaseOrderSummaryView | typeof createNewSentinel;

interface DraftPurchaseOrderPickerBrowserOptions extends FabricationPurchaseOrderSummaryTableOptions {
  onPoSelect?: (po: PurchaseOrderSummaryView) => void;
}

@customElement('wm-draftpurchaseorderpickerbrowser')
class DraftPurchaseOrderPickerBrowser extends FabricationPurchaseOrderSummaryTable {
  onPoSelect?: (po: PurchaseOrderSummaryView) => void;

  constructor(options: DraftPurchaseOrderPickerBrowserOptions) {
    super(options);
    this.onPoSelect = options.onPoSelect;
    this.clickrows = true;
  }

  override isDataLoadDelayed(): boolean {
    return false;
  }

  // A modal picker sizes to its content, not to a bounded flex region, so the fill-height
  // behaviour the list page wants would leave the table with no height to fill.
  override fillHeight(): boolean {
    return false;
  }

  // Why drop line-locked orders instead of listing them? The server refuses to add a job to one,
  // so the row could only ever fail on click, and the table has no per-row disabled state to
  // render instead.
  override excludeLineSetLocked(): boolean {
    return true;
  }

  // Drop the trailing cancel-hamburger column: in the picker the row itself is the action.
  override getColumns(): WebModuleTableColumnDef[] {
    return super.getColumns().filter(column => column.fieldName !== 'xx');
  }

  override async rowClicked(e: CustomEvent<{ table: WebModuleTable; item: PurchaseOrderSummaryView }>): Promise<void> {
    e.stopImmediatePropagation();
    e.preventDefault();

    this.eventHandler(async () => {
      this.onPoSelect?.(e.detail.item);
    });
  }
}

interface DraftPurchaseOrderPickerOptions extends ModalPickerDialogOptions<PurchaseOrderSummaryView> {
  branchId: string;
}

class DraftPurchaseOrderBrowserPicker extends ModalPickerDialog<PurchaseOrderSummaryView> {
  private browser: DraftPurchaseOrderPickerBrowser;
  private onCreateNew?: () => void;

  constructor(options: DraftPurchaseOrderPickerOptions & { onCreateNew?: () => void }) {
    super(options);
    this.onCreateNew = options.onCreateNew;

    this.browser = new DraftPurchaseOrderPickerBrowser({
      title: () => options.title ?? '',
      branchId: async () => options.branchId,
      states: [FabricationPurchaseOrderState.Draft],
      pageFragment: '',
      onPoSelect: po => this.select(po)
    });
  }

  protected get modalSize(): string {
    return 'modal-lg';
  }

  bodyTemplate() {
    return html`${this.browser}`;
  }

  footerTemplate() {
    return html`
      <webmodule-button size="small" variant="default" @click=${() => this.onCreateNew?.()}>
        ${LangFabricationPurchaseOrder.createNewPo()}
      </webmodule-button>
      <webmodule-button size="small" variant="default" @click=${() => this.cancel()}>
        ${LangFabricationPurchaseOrder.addToPoCancel()}
      </webmodule-button>
    `;
  }
}

export async function draftPurchaseOrderPicker(branchId: string): NullPromise<DraftPurchaseOrderPickerResult> {
  return new Promise<DraftPurchaseOrderPickerResult | null>(resolve => {
    const picker = new DraftPurchaseOrderBrowserPicker({
      onSelect: (selected: PurchaseOrderSummaryView | null) => {
        resolve(selected);
      },
      onCancel: () => resolve(null),
      onCreateNew: () => {
        resolve(createNewSentinel);
        void picker.hideModal();
      },
      title: LangFabricationPurchaseOrder.addToPoDialogTitle(),
      branchId
    });
    picker.showModal().then(() => resolve(null));
  });
}
